import { useEffect, useRef, useState, useMemo } from 'react';
import '../../styling/StagePicker.css';

export default function StagePicker({
  stages = [],
  selected = [],
  onChange,
  title = 'Stages to include',
  serviceType = '',
}) {
  const [isAllSelected, setIsAllSelected] = useState(selected.length === 0);

  // service-change bookkeeping
  const prevServiceRef = useRef(serviceType);
  const pendingServiceChangeRef = useRef(false);
  const suppressSelectedOnceRef = useRef(false);

  // ensure we only render unique stages (by code if present, else by name)
  const uniqueStages = useMemo(() => {
    const seen = new Set();
    return (stages || []).filter(s => {
      const k = (s.code ?? s.name) || '';
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [stages]);

  // use uniqueStages everywhere you previously used `stages` for rendering & checks
  const stageNames = useMemo(() => uniqueStages.map(s => s.name), [uniqueStages]);

  const requiredNames = useMemo(() => {
    const REQUIRED_LABELS = ['Booking Creation', 'Booking Creation In Sysfreight', 'ETD Port Of Loading', 'ETA Port Of Arrival'];
    return uniqueStages.filter(s => REQUIRED_LABELS.includes(s.name)).map(s => s.name);
  }, [uniqueStages]);

  const arraysEqualAsSets = (a,b) => {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    const A = new Set(a), B = new Set(b);
    if (A.size !== B.size) return false;
    for (const v of A) if (!B.has(v)) return false;
    return true;
  };

  const ensureRequired = (names) => Array.from(new Set([...(names || []), ...requiredNames]));

  const normalizeToVisible = (names) => {
    const visible = new Set(stageNames);
    return ensureRequired((names || []).filter(n => visible.has(n)));
  };

  const recomputeAllFlag = (sel) => {
    const checked = (arr, name) => requiredNames.includes(name) || (Array.isArray(arr) && arr.includes(name));
    setIsAllSelected(stages.length > 0 && stages.every(s => checked(sel, s.name)));
  };

  // mark a pending service change (don’t touch selection yet)
  useEffect(() => {
    if (prevServiceRef.current !== serviceType) {
      pendingServiceChangeRef.current = true;
    }
  }, [serviceType]);

  // react to stages present/change
  useEffect(() => {
    // nothing to do without visible stages
    if (stageNames.length === 0) {
      setIsAllSelected(false);
      return;
    }

    if (pendingServiceChangeRef.current) {
      // consume pending change ONCE when new stages exist → force ALL
      const next = normalizeToVisible(stageNames); // select all visible (+required)
      suppressSelectedOnceRef.current = true;      // prevent the selected-effect from "fixing" it
      if (!arraysEqualAsSets(next, selected)) onChange?.(next);
      setIsAllSelected(true);

      pendingServiceChangeRef.current = false;
      prevServiceRef.current = serviceType;
      return;
    }

    // normal path (no service switch pending)
    if (isAllSelected) {
      // when All is on, reconcile to ALL visible (prevents accidental trimming)
      const next = normalizeToVisible(stageNames);
      if (!arraysEqualAsSets(next, selected)) onChange?.(next);
      // isAllSelected is already true, no change
      return;
    }

    // if not All: keep selection valid against current stages
    if (selected.length > 0) {
      const next = normalizeToVisible(selected);
      if (!arraysEqualAsSets(next, selected)) onChange?.(next);
      recomputeAllFlag(next);
    } else {
      // empty selection (external) → default to ALL
      const next = normalizeToVisible(stageNames);
      if (!arraysEqualAsSets(next, selected)) onChange?.(next);
      setIsAllSelected(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages]);

  // keep All flag in sync with external selected changes
  useEffect(() => {
    // swallow the *immediate next* selected change we ourselves triggered for service change
    if (suppressSelectedOnceRef.current) {
      suppressSelectedOnceRef.current = false;
      recomputeAllFlag(selected);
      return;
    }

    const next = normalizeToVisible(selected);
    if (!arraysEqualAsSets(next, selected)) {
      onChange?.(next); // only fix if truly invalid vs current stages
    }
    recomputeAllFlag(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // interactions
  const toggleAll = () => {
    if (isAllSelected) {
      setIsAllSelected(false);
      onChange?.(normalizeToVisible([]));       // keep only required
    } else {
      setIsAllSelected(true);
      onChange?.(normalizeToVisible(stageNames)); // include all
    }
  };

  const toggleOne = (name) => {
    if (requiredNames.includes(name)) return;   // cannot toggle required
    setIsAllSelected(false);
    const isSel = Array.isArray(selected) && selected.includes(name);
    onChange?.(
      normalizeToVisible(
        isSel ? selected.filter(s => s !== name) : [...(selected || []), name]
      )
    );
  };

  const isChecked = (name) =>
    requiredNames.includes(name) || isAllSelected || (Array.isArray(selected) && selected.includes(name));

  const isDisabled = (name) => requiredNames.includes(name);

  return (
    <div className="card">
      <div className="card-header fw-semibold">{title}</div>
      <div className="card-body">
        <div className="form-check mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="stage_all"
            checked={isAllSelected}
            onChange={toggleAll}
            disabled={!serviceType}
          />
          <label className="form-check-label" htmlFor="stage_all">All</label>
        </div>

        <div className="row">
          {uniqueStages.map((s, i) => (
            <div key={(s.code ?? s.name) + '-' + i} className="col-6 col-md-4 col-lg-6 mb-2">
              <div className="stagepicker-item">
                <input
                  className="form-check-input stagepicker-checkbox"
                  type="checkbox"
                  id={`stage_${(s.code ?? s.name) + '-' + i}`}
                  onChange={() => toggleOne(s.name)}
                  checked={isChecked(s.name)}
                  disabled={isDisabled(s.name)}
                />
                <label
                  className="form-check-label stagepicker-label"
                  htmlFor={`stage_${(s.code ?? s.name) + '-' + i}`}
                >
                  {s.name}
                </label>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 alert alert-secondary small">
          <ul className="mb-0">
            <li><strong>Booking Creation</strong> is selected only if there is a <strong>Booking Ref</strong> but no <strong>Sysfreight Ref</strong>.</li>
            <li><strong>ETD Port Of Loading</strong> and <strong>ETA Port Of Arrival</strong> are compulsory.</li>
            <li>Checking <strong>HBL Creation</strong> will require selecting a <strong>Delivery Agent Type</strong>.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
